import Cookies from 'js-cookie'
import { Languages, MessageCircle } from 'lucide-react'
import { i18n } from 'next.config'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { resolveHeaderConfig } from '@/lib/header-footer-config'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'
import useScroll from '@/hooks/use-scroll'

import DefaultNavRightContent from '@/components/layouts/nav/default-nav-right-content'
import { getGeneralV1OverlayClassName, getHeaderShellClassName, headerContentWidthClassNames } from '@/components/layouts/nav/header-shell-styles'
import MainNavContent from '@/components/layouts/nav/main-nav-content'
import MobileNavContent from '@/components/layouts/nav/mobile-nav-content'
import SearchBarCommand from '@/components/layouts/nav/search-bar-command'

import { UNREAD_COUNT } from '@/constant/ui'
import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'

import { UserAccountNav } from './user-account-nav'
import { UserNotificationNav } from './user-notification-nav'

import { HeaderConfig } from '@/types/header-footer-config'

interface NavBarProps {
  children?: React.ReactNode
  rightElements?: React.ReactNode
  scroll?: boolean
  headerConfig?: HeaderConfig
}

function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      // If clicking inside the ref element, do nothing
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return
      }
      handler()
    }
    document.addEventListener('mousedown', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
    }
  }, [ref, handler])
}

export const LanguageSwitcher = () => {
  const router = useRouter()
  const { locale } = router
  const availableLocales = i18n?.locales
  const localeLabels: Record<string, string> = {
    en: 'English',
    vi: 'Tiếng Việt',
  }
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const reloadPaths = ['/admin', '/my-courses', '/hall-of-fame', '/blogs']

  useOnClickOutside(dropdownRef, () => setIsOpen(false))

  const handleLanguageChange = async (newLocale: string) => {
    Cookies.set('NEXT_LOCALE', newLocale)
    await router.push(router.pathname, router.asPath, { locale: newLocale }).then(() => {
      setIsOpen(false)
      if (reloadPaths.some(path => router.pathname.startsWith(path))) {
        router.reload()
      }
    })
  }

  return (
    <div className="relative sm:mr-0" ref={dropdownRef}>
      <div
        className="cursor-pointer hover:bg-gray-100 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <Languages className="size-6" />
      </div>
      {isOpen && (
        <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-32 rounded-md border border-gray-200 bg-white shadow-lg z-50">
          <ul>
            {availableLocales?.map(loc => (
              <li key={loc}>
                <button
                  onClick={() => handleLanguageChange(loc)}
                  className={`block w-full text-center px-4 py-2 text-sm ${
                    locale === loc ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50'
                  }`}
                >
                  {localeLabels[loc]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export const MessageIcon = () => {
  const { openChat, totalUnreadCount } = useChat()
  const { user } = useAuth()

  const handleMessageClick = () => {
    if (user) {
      openChat()
    }
  }

  if (!user) return null

  return (
    <div
      className="cursor-pointer hover:bg-gray-100 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full relative"
      onClick={handleMessageClick}
    >
      <MessageCircle className="size-6" />
      {totalUnreadCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[1.5rem] px-1.5 h-5 inline-flex items-center justify-center whitespace-nowrap leading-none font-medium">
          {totalUnreadCount > UNREAD_COUNT.MAX_DISPLAY ? UNREAD_COUNT.MAX_DISPLAY_TEXT : totalUnreadCount}
        </span>
      )}
    </div>
  )
}

function RightNavContent() {
  const { isLoading, user } = useAuth()
  if (isLoading) return null
  if (user)
    return (
      <>
        <UserNotificationNav />
        <UserAccountNav user={user} />
      </>
    )
  return <DefaultNavRightContent />
}

function NavBar({ scroll, children, rightElements, headerConfig }: NavBarProps) {
  const scrolled = useScroll(64)
  const { text, lang } = useLocale()
  const resolvedHeaderConfig = resolveHeaderConfig(headerConfig, text, lang)
  const { user } = useAuth()
  const shouldStick = resolvedHeaderConfig.sticky
  const isOverlay = resolvedHeaderConfig.templatePreset === 'general-v1-overlay'

  const headerClassName = isOverlay
    ? getGeneralV1OverlayClassName(scrolled)
    : getHeaderShellClassName(shouldStick, resolvedHeaderConfig.showBorder, scroll, scrolled)

  return (
    <header
      className={cn(
        headerClassName,
        isOverlay && 'text-white [&_a]:text-white [&_button]:text-white'
      )}
    >
      <div
        className={cn(
          'flex h-16 w-full items-center justify-between px-2 py-3 sm:px-4 md:h-20 md:py-4 lg:px-8',
          isOverlay && 'h-[var(--gv1-header-height)] md:h-[var(--gv1-header-height)] px-[var(--gv1-header-px)]',
          headerContentWidthClassNames[resolvedHeaderConfig.containerWidth]
        )}
      >
        <MainNavContent items={resolvedHeaderConfig.navItems} headerConfig={resolvedHeaderConfig}>
          {children}
        </MainNavContent>
        <MobileNavContent headerConfig={resolvedHeaderConfig} />
        {resolvedHeaderConfig.showRightSection && (
          <div
            className={cn(
              'flex shrink-0 items-center gap-1.5 sm:gap-2',
              user &&
                !isOverlay &&
                'rounded-full border border-border/60 bg-background/80 p-1 shadow-sm supports-backdrop-blur:bg-background/60 sm:p-1.5',
              isOverlay && 'text-white'
            )}
          >
            {user && <SearchBarCommand />}
            {rightElements}
            {lang === 'vi' && !isOverlay && <MessageIcon />}
            <RightNavContent />
          </div>
        )}
      </div>
    </header>
  )
}

export default NavBar
